/**
 * The only place a bearer token is attached and the only place `fetch` is
 * called. Components call the typed helpers below, never fetch directly.
 */
import type {
  ActivityPage,
  AddLeadTagBody,
  AnalyseCallBody,
  AnalyseCallResponse,
  AnalyseChatBody,
  AnalyseChatResponse,
  AttachManyBody,
  AttachManyResponse,
  BulkCloseLeadsBody,
  BulkCloseLeadsResponse,
  BulkTagLeadsBody,
  BulkTagLeadsResponse,
  CallAnalysis,
  CallHourStatsResponse,
  CallPage,
  CallQueuePage,
  CallQueueQuery,
  CampaignOption,
  ChangeLeadStatusBody,
  ChatAnalysis,
  ClearCallReviewResponse,
  CompanyOption,
  CompleteFollowUpBody,
  Contact,
  ContactBriefing,
  ContactPage,
  ConvertLeadBody,
  ConvertLeadResponse,
  CreateContactBody,
  CreateFollowUpBody,
  CreateLeadBody,
  CreateLeadResponse,
  CreateNoteBody,
  CreateTagBody,
  CreateUserBody,
  CrmUser,
  CustomerInsight,
  DashboardStats,
  DashboardStatsQuery,
  DaySlots,
  FollowUp,
  FollowUpCalendar,
  FollowUpCalendarQuery,
  FollowUpPage,
  FreeSlotsQuery,
  GeminiSettings,
  Lead,
  LeadArrivals,
  LeadArrivalsQuery,
  LeadPage,
  LeadTag,
  ListActivitiesQuery,
  ListCallsQuery,
  ListContactsQuery,
  ListFollowUpsQuery,
  ListLeadsQuery,
  ListUsersQuery,
  MeResponse,
  MyDayQuery,
  MyDayResponse,
  NewSellersQuery,
  NewSellersResponse,
  Note,
  PipelineStageOption,
  RescheduleFollowUpBody,
  Tag,
  TagOption,
  TodayQuery,
  TodayResponse,
  UpdateContactBody,
  UpdateGeminiSettingsBody,
  UpdateLeadBody,
  UpdateMyTimezoneBody,
  UpdateNoteBody,
  UpdateUserBody,
  UserOption,
  WelcomeResponse,
  WhatsappImportBody,
  WhatsappImportPreview,
  WhatsappImportPreviewBody,
  WhatsappImportResult,
} from '@crm/contracts';
import { NOTES_PAGE_MAX } from '@crm/contracts';
import { ApiError, apiFetch, qs } from './http';

// Re-exported so the many `import { ApiError } from '@/api/client'` sites keep
// working, and so there is still one obvious place to import it from.
export { ApiError };

export const api = {
  me: () => apiFetch<MeResponse>('/api/me'),
  /**
   * Which clock my screens run on. Mine only — the route takes no id, so this
   * cannot be pointed at a colleague.
   */
  setMyTimezone: (body: UpdateMyTimezoneBody) =>
    apiFetch<MeResponse>('/api/me/timezone', { method: 'PATCH', body: JSON.stringify(body) }),

  calls: {
    list: (q: ListCallsQuery) => apiFetch<CallPage>(`/api/calls${qs(q as never)}`),
    /**
     * When this organisation actually gets answered.
     *
     * Read by the forms so a follow-up time stops defaulting to a hard-coded
     * 10:00 — an hour that, on this account, has too few dials to judge at
     * all while noon answers 83%.
     */
    hourStats: () => apiFetch<CallHourStatsResponse>('/api/calls/hour-stats'),

    /**
     * §8's analysis of one call, transcript included.
     *
     * A 404 is the ordinary case — most calls have never been analysed — so
     * the hook that uses this treats it as "nothing yet" rather than an
     * error, and the panel offers the Analyse button instead.
     */
    analysis: (id: number) => apiFetch<CallAnalysis>(`/api/calls/${id}/analysis`),

    /**
     * Queue one call. Answers 200 with a REASON even when nothing was
     * queued — "no recording", "too short" — because the alternative is a
     * button that appears to do nothing.
     */
    analyse: (id: number, body: AnalyseCallBody = {}) =>
      apiFetch<AnalyseCallResponse>(`/api/calls/${id}/analysis`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * A manager saying they have listened to a flagged call.
     *
     * No body: who and when are taken from the session server-side, because
     * a client able to assert "reviewed by" could clear a flag in somebody
     * else's name.
     */
    clearReview: (id: number) =>
      apiFetch<ClearCallReviewResponse>(`/api/calls/${id}/analysis/review`, { method: 'POST' }),
  },

  /**
   * The start-of-day page, in one request.
   *
   * One endpoint rather than three composed in the browser: three calls
   * meant three snapshots and totals the client derived from pages each
   * capped on their own, so the number at the top could disagree with the
   * rows under it.
   */
  today: {
    get: (q: TodayQuery) => apiFetch<TodayResponse>(`/api/today${qs(q as never)}`),
  },

  /** The start-of-day summary: counts, promises and service health. */
  welcome: {
    get: () => apiFetch<WelcomeResponse>('/api/welcome'),
  },

  /** §8's work queue: which analysed call to pick up next. */
  callQueue: {
    list: (q: CallQueueQuery) => apiFetch<CallQueuePage>(`/api/call-queue${qs(q as never)}`),
  },

  /**
   * Bringing an exported WhatsApp conversation onto a contact.
   *
   * On the CONTACT because an export is one conversation with one person,
   * so the person is already chosen and there is no phone matching to get
   * wrong — the largest source of a bad import.
   */
  whatsappImport: {
    preview: (contactId: string, body: WhatsappImportPreviewBody) =>
      apiFetch<WhatsappImportPreview>(`/api/contacts/${contactId}/whatsapp-import/preview`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    run: (contactId: string, body: WhatsappImportBody) =>
      apiFetch<WhatsappImportResult>(`/api/contacts/${contactId}/whatsapp-import`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  /** Admin only, both of them: the switch decides whether audio leaves here. */
  gemini: {
    get: () => apiFetch<GeminiSettings>('/api/settings/gemini'),
    update: (body: UpdateGeminiSettingsBody) =>
      apiFetch<GeminiSettings>('/api/settings/gemini', { method: 'PATCH', body: JSON.stringify(body) }),
  },

  followUps: {
    list: (q: ListFollowUpsQuery) => apiFetch<FollowUpPage>(`/api/follow-ups${qs(q as never)}`),
    counts: (mineOnly?: boolean) =>
      apiFetch<{ overdue: number; today: number; upcoming: number; completed: number }>(
        `/api/follow-ups/counts${qs({ mineOnly: mineOnly ? 'true' : undefined })}`,
      ),
    /**
     * The calendar's one read — agenda, month and week all render from it.
     * Booleans are spelled out rather than passed through `qs`, which would
     * send `mineOnly=false` and make "everyone" look like a filter.
     */
    calendar: (q: FollowUpCalendarQuery) =>
      apiFetch<FollowUpCalendar>(
        `/api/follow-ups/calendar${qs({
          from: q.from,
          to: q.to,
          includeOverdue: q.includeOverdue ? 'true' : undefined,
          mineOnly: q.mineOnly ? 'true' : undefined,
          ownerId: q.ownerId,
          contactId: q.contactId,
          leadId: q.leadId,
          limit: q.limit,
        })}`,
      ),
    /** Which five-minute slots a day still has free, in one owner's diary. */
    freeSlots: (q: FreeSlotsQuery) =>
      apiFetch<DaySlots>(
        `/api/follow-ups/free-slots${qs({
          date: q.date,
          ownerId: q.ownerId,
          unassigned: q.unassigned,
          excludeFollowUpId: q.excludeFollowUpId,
        })}`,
      ),
    create: (body: CreateFollowUpBody) =>
      apiFetch<{ id: string }>('/api/follow-ups', { method: 'POST', body: JSON.stringify(body) }),
    /**
     * Takes the whole body rather than a status, because closing one now
     * carries what happened: a demo that went well and a no-show were
     * indistinguishable in the record, and `missed` is the third answer.
     */
    close: (id: string, body: CompleteFollowUpBody) =>
      apiFetch<{ ok: boolean }>(`/api/follow-ups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** A move, not an edit — the row keeps its id so the timeline stays one thread. */
    reschedule: (id: string, body: RescheduleFollowUpBody) =>
      apiFetch<FollowUp>(`/api/follow-ups/${id}/reschedule`, { method: 'POST', body: JSON.stringify(body) }),
  },

  activities: {
    list: (q: ListActivitiesQuery) => apiFetch<ActivityPage>(`/api/activities${qs(q as never)}`),
  },

  notes: {
    create: (body: CreateNoteBody) => apiFetch<Note>('/api/notes', { method: 'POST', body: JSON.stringify(body) }),
    /**
     * The limit is sent explicitly and is the contract maximum. Omitting it
     * does not mean "all" — the route falls back to 50 and drops the rest
     * without saying so, which read as a complete history on the profile.
     * Both callers share one query key, so the limit is fixed here rather
     * than taken as an argument: two callers asking for different sizes would
     * fight over the same cache entry.
     */
    listForContact: (contactId: string) => apiFetch<Note[]>(`/api/notes${qs({ contactId, limit: NOTES_PAGE_MAX })}`),
    /** `notes.id` is a bigint identity, so the path parameter is a number. */
    update: (id: number, body: UpdateNoteBody) =>
      apiFetch<Note>(`/api/notes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: number) => apiFetch<{ ok: boolean }>(`/api/notes/${id}`, { method: 'DELETE' }),
  },

  /**
   * §3 and §9. `list` is the shared vocabulary; the lead-scoped calls are one
   * attachment each, and `removeFromLead` takes the TAG id, not the taggables
   * row — that is the id the chip carries back.
   */
  tags: {
    list: (search?: string) => apiFetch<Tag[]>(`/api/tags${qs({ search })}`),
    create: (body: CreateTagBody) => apiFetch<Tag>('/api/tags', { method: 'POST', body: JSON.stringify(body) }),
    forLead: (leadId: string) => apiFetch<LeadTag[]>(`/api/leads/${leadId}/tags`),
    addToLead: (leadId: string, body: AddLeadTagBody) =>
      apiFetch<LeadTag>(`/api/leads/${leadId}/tags`, { method: 'POST', body: JSON.stringify(body) }),
    removeFromLead: (leadId: string, tagId: string) =>
      apiFetch<{ ok: boolean }>(`/api/leads/${leadId}/tags/${tagId}`, { method: 'DELETE' }),
    /** One tag onto many leads. Leads that already carry it come back skipped. */
    bulkTag: (body: BulkTagLeadsBody) =>
      apiFetch<BulkTagLeadsResponse>('/api/leads/bulk-tag', { method: 'POST', body: JSON.stringify(body) }),
  },

  contacts: {
    /**
     * What to know before ringing them — computed, never generated.
     * See docs/12: every field is a fact with a date beside it.
     */
    briefing: (id: string) => apiFetch<ContactBriefing>(`/api/contacts/${id}/briefing`),

    /**
     * What the CALLS say about them — the model's read, next door to the
     * briefing's computed facts and deliberately not mixed into it.
     */
    callInsights: (id: string) => apiFetch<CustomerInsight>(`/api/contacts/${id}/call-insights`),

    /** §11 — what Gemini read in the WhatsApp conversation. */
    chatAnalysis: (id: string) => apiFetch<ChatAnalysis>(`/api/contacts/${id}/chat-analysis`),
    analyseChat: (id: string, body: AnalyseChatBody = {}) =>
      apiFetch<AnalyseChatResponse>(`/api/contacts/${id}/chat-analysis`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    clearChatReview: (id: string) =>
      apiFetch<{ contactId: string; reviewedAt: string }>(`/api/contacts/${id}/chat-analysis/review`, {
        method: 'POST',
      }),
    list: (q: ListContactsQuery) =>
      apiFetch<ContactPage>(`/api/contacts${qs(q as Record<string, string | number | undefined>)}`),
    get: (id: string) => apiFetch<Contact>(`/api/contacts/${id}`),
    create: (body: CreateContactBody) =>
      apiFetch<Contact>('/api/contacts', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateContactBody) =>
      apiFetch<Contact>(`/api/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },

  leads: {
    list: (q: ListLeadsQuery) => apiFetch<LeadPage>(`/api/leads${qs(q as never)}`),
    /** The arrivals sheet: every enquiry grouped by the day it came in. */
    arrivals: (q: LeadArrivalsQuery) => apiFetch<LeadArrivals>(`/api/leads/arrivals${qs(q as never)}`),
    get: (id: string) => apiFetch<Lead>(`/api/leads/${id}`),
    create: (body: CreateLeadBody) =>
      apiFetch<CreateLeadResponse>('/api/leads', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateLeadBody) =>
      apiFetch<Lead>(`/api/leads/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    changeStatus: (id: string, body: ChangeLeadStatusBody) =>
      apiFetch<Lead>(`/api/leads/${id}/status`, { method: 'POST', body: JSON.stringify(body) }),
    /** 201 with both halves: the lead, now converted, and the deal it produced. */
    convert: (id: string, body: ConvertLeadBody) =>
      apiFetch<ConvertLeadResponse>(`/api/leads/${id}/convert`, { method: 'POST', body: JSON.stringify(body) }),
    /**
     * Take an unclaimed lead. No body — the claimant is whoever is asking.
     *
     * 409 when somebody was quicker, which the caller shows rather than
     * swallowing: at 9am two agents looking at the same unclaimed lead is
     * the ordinary case, and silence would leave both believing they had it.
     */
    claim: (id: string) => apiFetch<Lead>(`/api/leads/${id}/claim`, { method: 'POST' }),
    /** Closing a batch of dead leads. Already-closed ones come back as skipped. */
    bulkClose: (body: BulkCloseLeadsBody) =>
      apiFetch<BulkCloseLeadsResponse>('/api/leads/bulk-close', { method: 'POST', body: JSON.stringify(body) }),
  },

  /**
   * §13. `my-day` takes no owner — the route always answers for the signed-in
   * operator, so there is nothing to pass.
   */
  dashboard: {
    myDay: (q: MyDayQuery = {}) => apiFetch<MyDayResponse>(`/api/dashboard/my-day${qs(q as never)}`),
    newSellers: (q: NewSellersQuery = {}) =>
      apiFetch<NewSellersResponse>(`/api/dashboard/new-sellers${qs(q as never)}`),
    stats: (q: DashboardStatsQuery) => apiFetch<DashboardStats>(`/api/dashboard/stats${qs(q as never)}`),
  },

  /**
   * §14 operator administration. Distinct from `lookups.users`, which is the
   * thin {id, name} option list every form uses: these carry the role, the
   * reporting line and the visibility flag, and only an admin may write them.
   *
   * Booleans are spelled out rather than passed through `qs`, which would send
   * `includeInactive=false` and make "hide deactivated" read as a filter.
   */
  users: {
    list: (q: ListUsersQuery) =>
      apiFetch<CrmUser[]>(
        `/api/users${qs({ search: q.search, role: q.role, includeInactive: q.includeInactive ? 'true' : undefined })}`,
      ),
    get: (id: string) => apiFetch<CrmUser>(`/api/users/${id}`),
    create: (body: CreateUserBody) => apiFetch<CrmUser>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpdateUserBody) =>
      apiFetch<CrmUser>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },

  /**
   * Select and combobox options. Cached hard by the query layer — these change
   * rarely, and refetching the operator list on every form open is waste.
   */
  requirements: {
    /** Link one catalogue entry to several leads in one transaction. */
    attachMany: (catalogId: string, body: AttachManyBody) =>
      apiFetch<AttachManyResponse>(`/api/requirements/catalog/${catalogId}/leads`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  lookups: {
    users: () => apiFetch<UserOption[]>('/api/lookups/users'),
    companies: (search?: string) => apiFetch<CompanyOption[]>(`/api/lookups/companies${qs({ search })}`),
    campaigns: () => apiFetch<CampaignOption[]>('/api/lookups/campaigns'),
    tags: () => apiFetch<TagOption[]>('/api/lookups/tags'),
    /** Default pipeline first, then by position — the convert dialog's stage select. */
    pipelineStages: () => apiFetch<PipelineStageOption[]>('/api/lookups/pipeline-stages'),
  },
};
